/**
 * 運営ポータル（ops.kenzai-order.potential-x.com・D-26）。
 *
 * ログインは**リダイレクト方式**（`/api/v1/auth/ops/google/start` へ遷移 → Google →
 * コールバックで Cookie が付いて戻る）＝SPA 側に OAuth のコードを持たない。
 *
 * ログイン後は **S15 テナント管理**（08 §5）＝一覧・開設ウィザード・詳細（有効化/停止/再開）。
 * 監視・利用状況は後続（08 §4）。
 */

import { auth, type OpsMe } from "@kenzai/api-client";
import { config } from "@kenzai/config";
import { AuthCard, Button } from "@kenzai/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { Shell } from "./Shell";
import { Audit } from "./pages/Audit";
import { RequestQueue } from "./pages/RequestQueue";
import { Overview } from "./pages/Overview";
import { TenantDetail } from "./pages/TenantDetail";
import { TenantList } from "./pages/TenantList";
import { TenantOpen } from "./pages/TenantOpen";
import { Usage } from "./pages/Usage";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

type State = { kind: "loading" } | { kind: "anon" } | { kind: "authed"; me: OpsMe };

export function App() {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    auth
      .opsMe()
      .then((me) => setState({ kind: "authed", me }))
      .catch(() => setState({ kind: "anon" }));
  }, []);

  if (state.kind === "loading") return <AuthCard title="読み込み中…">{null}</AuthCard>;

  if (state.kind === "anon") {
    return (
      <AuthCard title="建材オーダー｜運営ポータル" subtitle="PotentialX の Google アカウントでログイン">
        {/* ★相対パスにしない：フロントと API が別オリジンの環境（prod の api. サブドメイン）では
            Pages の SPA フォールバックに吸われてログイン画面が再表示されるだけになる。
            apiBase が空（dev・stg のプロキシ経由）ならこれまでどおり相対に解決される。 */}
        <Button fullWidth onClick={() => (window.location.href = `${config.apiBase}/api/v1/auth/ops/google/start`)}>
          Google でログイン
        </Button>
        <p
          style={{
            marginTop: "var(--kz-space-5)",
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-ink-muted)",
          }}
        >
          運営として登録されているアカウントのみログインできます。
        </p>
      </AuthCard>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Shell me={state.me} onLogout={() => setState({ kind: "anon" })} />}>
            <Route path="/" element={<TenantList />} />
            <Route path="/overview" element={<Overview />} />
            <Route path="/usage" element={<Usage />} />
            <Route path="/audit" element={<Audit />} />
            {/* 08 §10 発注代行キュー（T-D40・D-69）＝operator 宛の依頼だけが出る */}
            <Route path="/requests" element={<RequestQueue />} />
            <Route path="/open" element={<TenantOpen />} />
            <Route path="/tenants/:tenantId" element={<TenantDetail />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
